/**
 * Notifications page — the in-app notification centre.
 *
 * The page keeps everything it always owned: fetching, the search / status /
 * type filters, selection, the bulk actions and pagination. What changed is
 * the body — instead of one flat list, the rows are handed to
 * {@link NotificationCentre}, which groups them by module, coalesces repeat
 * updates about one object into a single expandable row, and hides modules the
 * user has no access to.
 *
 * @link components/notifications for the grouping engine and the shared bell.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { notificationsAPI, type Notification } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Bell,
  Check,
  CheckCheck,
  Filter,
  Moon,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

import { useNotificationModuleAccess } from '../components/notifications/useNotificationModuleAccess';
import {
  NotificationCentre,
  notifyNotificationsUpdated,
} from '../components/notifications';

/**
 * Server-side category filter. These are the `type` values the list endpoint
 * understands (see `NOTIFICATION_CATEGORY_MAP` in
 * `notifications/views/notification_views.py`) — the module *grouping* below
 * is driven by the template catalogue, not by this list.
 */
const notificationCategories = [
  { value: 'all', label: 'All Types' },
  { value: 'po', label: 'Purchase Orders' },
  { value: 'ncr', label: 'Quality (NCR)' },
  { value: 'supplier', label: 'Suppliers' },
  { value: 'rfq', label: 'RFQ' },
  { value: 'user', label: 'Users' },
  { value: 'document', label: 'Documents' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'calloff', label: 'Call-Off' },
  { value: 'system', label: 'System' },
];

const PAGE_SIZE = 20;

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterRead, setFilterRead] = useState<string>('all');
  const [showBackground, setShowBackground] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // Unread across the WHOLE filtered set, not just the page in hand. Counting
  // the loaded slice made the header read "215 total, 20 unread" when all 215
  // were unread — 20 being merely the page size.
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  // The page must count what it can actually show. The centre hides sections
  // for modules the user lacks, so without sending the same domain list the
  // header read "2 total, 2 unread" over a list of one — and disagreed with
  // the sidebar badge, which has always scoped by domain.
  const { visibleDomains, isLoading: accessLoading } = useNotificationModuleAccess();
  const domainsParam = visibleDomains.join(',');

  const fetchNotifications = useCallback(async () => {
    // Wait for the domain list: fetching first would ask unscoped and flash
    // counts that include modules this user cannot see.
    if (accessLoading) return;
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page,
        page_size: PAGE_SIZE,
      };

      if (searchQuery) params['search'] = searchQuery;
      if (filterRead === 'unread') params['is_read'] = false;
      if (filterRead === 'read') params['is_read'] = true;
      if (filterType && filterType !== 'all') params['type'] = filterType;
      // Background-tier rows are only fetched when the Activity toggle is on.
      params['include_background'] = showBackground;
      if (domainsParam) params['domains'] = domainsParam;

      // The unread total comes from the server, scoped identically to the
      // list — same domains, same background tier — so the header, the list
      // and the sidebar badge cannot disagree.
      const countParams: Record<string, string | boolean> = {
        include_background: showBackground,
      };
      if (domainsParam) countParams['domains'] = domainsParam;

      const [response, unread] = await Promise.all([
        notificationsAPI.getNotifications(params),
        // Promise.resolve, not a bare .catch: the count is a nice-to-have, and
        // a caller that hands back something non-thenable must not be able to
        // take the whole page down with it.
        Promise.resolve(notificationsAPI.getUnreadCount(countParams))
          .catch(() => null),
      ]);
      setNotifications(response.data.results);
      setTotalCount(response.data.count);
      if (unread) setUnreadCount(unread.data.unread_count ?? 0);
      setError(null);
    } catch (err: unknown) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterRead, filterType, searchQuery, showBackground, domainsParam, accessLoading]);

  useEffect(() => {
    fetchNotifications();
    // accessLoading/domainsParam are deps too: the first render happens before
    // permissions resolve, and fetchNotifications no-ops until they do — so
    // without re-running here the page would sit on its loading state forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterRead, filterType, showBackground, accessLoading, domainsParam]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (page === 1) {
        fetchNotifications();
      } else {
        setPage(1);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleSelectAll = () => {
    if (selectedIds.size === notifications.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(notifications.map((n) => n.id)));
    }
  };

  /** Tick or untick a whole entry (one row, or every update in a thread). */
  const handleSelectionChange = (ids: number[], selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (selected) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  /**
   * Marks one row, a whole thread or a whole module section as read.
   *
   * CR-4756: already-read ids never reach the backend, and the confirmation
   * counts what actually changed.
   */
  const handleMarkRead = async (ids: number[]) => {
    const unreadIds = ids.filter((id) => {
      const target = notifications.find((n) => n.id === id);
      return target && !target.is_read;
    });
    if (unreadIds.length === 0) return;

    try {
      let updated = unreadIds.length;
      if (unreadIds.length === 1) {
        await notificationsAPI.markAsRead(unreadIds[0]);
      } else {
        const response = await notificationsAPI.markSelectedAsRead(unreadIds);
        updated = response.data.updated_count ?? unreadIds.length;
      }
      setNotifications((prev) =>
        prev.map((n) => (unreadIds.indexOf(n.id) !== -1 ? { ...n, is_read: true } : n))
      );
      toast.success(`${updated} notification${updated === 1 ? '' : 's'} marked as read`);
      // CR-4756: refresh the bells immediately instead of waiting for the poll.
      notifyNotificationsUpdated();
    } catch (err: unknown) {
      console.error('Error marking notifications as read:', err);
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    const hasUnread = ids.some((id) => {
      const target = notifications.find((n) => n.id === id);
      return target && !target.is_read;
    });
    if (!hasUnread) {
      toast.info('Selected notifications are already read');
      setSelectedIds(new Set());
      return;
    }
    await handleMarkRead(ids);
    setSelectedIds(new Set());
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await notificationsAPI.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success(response.data.message);
      // CR-3938: Notify the bells to refresh
      notifyNotificationsUpdated();
    } catch (err: unknown) {
      console.error('Error marking all as read:', err);
      toast.error('Failed to mark all notifications as read');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await notificationsAPI.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setTotalCount((prev) => prev - 1);
      toast.success('Notification deleted');
      // CR-3938: Notify the bells to refresh
      notifyNotificationsUpdated();
    } catch (err: unknown) {
      console.error('Error deleting notification:', err);
      toast.error('Failed to delete notification');
    }
  };

  const handleDeleteAllRead = async () => {
    try {
      const response = await notificationsAPI.deleteAllRead();
      fetchNotifications();
      toast.success(response.data.message);
      // CR-3938: Notify the bells to refresh
      notifyNotificationsUpdated();
    } catch (err: unknown) {
      console.error('Error deleting read notifications:', err);
      toast.error('Failed to delete read notifications');
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkRead([notification.id]);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };


  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const hasUnreadSelected = notifications.some(
    (n) => selectedIds.has(n.id) && !n.is_read
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Notifications
          </h1>
          <p className="text-muted-foreground mt-1">
            {totalCount} total, {unreadCount} unread
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchNotifications}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleMarkAllAsRead}>
                <CheckCheck className="h-4 w-4 mr-2" />
                Mark all as read
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDeleteAllRead} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete all read
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterRead} onValueChange={setFilterRead}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread</SelectItem>
                <SelectItem value="read">Read</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {notificationCategories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showBackground ? 'default' : 'outline'}
              size="sm"
              className="sm:w-auto"
              onClick={() => setShowBackground((prev) => !prev)}
              aria-pressed={showBackground}
              title="Background-tier updates are kept out of the bell and this list by default"
            >
              <Moon className="h-4 w-4 mr-2" />
              Activity
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Selection toolbar */}
      <div className="flex flex-wrap items-center gap-4 px-1">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={selectedIds.size === notifications.length && notifications.length > 0}
            onCheckedChange={handleSelectAll}
            aria-label="Select all notifications"
          />
          <span className="text-sm text-muted-foreground">Select all</span>
        </div>
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 rounded-lg bg-blue-50 px-3 py-2 dark:bg-blue-900/20">
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleMarkSelectedAsRead}
              disabled={!hasUnreadSelected}
              title={
                hasUnreadSelected
                  ? 'Mark selected as read'
                  : 'All selected notifications are already read'
              }
            >
              <Check className="h-4 w-4 mr-2" />
              Mark as read
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Grouped centre */}
      <NotificationCentre
        notifications={notifications}
        loading={loading}
        error={error}
        onRetry={fetchNotifications}
        selectedIds={selectedIds}
        onSelectionChange={handleSelectionChange}
        onOpen={handleNotificationClick}
        onMarkRead={handleMarkRead}
        onDelete={handleDelete}
        showBackground={showBackground}
        onToggleBackground={() => setShowBackground((prev) => !prev)}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * PAGE_SIZE + 1} to{' '}
            {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
